import React, { useEffect, useState } from 'react';
import { EditIcon } from './ActionIcons';

// Inline-форма приёма ручной оплаты. Раньше дублировалась в OrderModal и в модалке
// CertificatesPage — теперь общий компонент. Рендерится прямо в форме (не как overlay).
//
// Контракт onConfirm:
//   await onConfirm({ method: 'QR'|..., paid_at: ISOString, comment: string|null });
// Если onConfirm бросит ошибку — её сообщение покажется внутри диалога.

const DEFAULT_METHODS = [
  { key: 'QR',       label: 'QR' },
  { key: 'Карта',    label: 'Карта' },
  { key: 'Наличные', label: 'Наличные' },
  { key: 'Р/с',      label: 'Р/с' },
];

const EMPTY_FORM = { method: '', paid_at_date: '', paid_at_time: '', comment: '', editTime: false, error: '' };

export default function InlinePaymentDialog({
  open,
  onClose,
  onConfirm,
  busy = false,
  methods = DEFAULT_METHODS,
  title = 'Приём оплаты',
  extraSlot = null,           // например, чекбокс «Оплатить все заказы подписки»
  commentPlaceholder = 'Комментарий (необязательно)',
}) {
  const [form, setForm] = useState(EMPTY_FORM);

  // Сбрасываем форму при каждом открытии.
  useEffect(() => {
    if (open) setForm(EMPTY_FORM);
  }, [open]);

  if (!open) return null;

  const setNow = () => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    setForm(prev => ({
      ...prev,
      paid_at_date: `${pad(now.getDate())}.${pad(now.getMonth() + 1)}.${now.getFullYear()}`,
      paid_at_time: `${pad(now.getHours())}:${pad(now.getMinutes())}`,
      editTime: false, error: '',
    }));
  };

  const toggleEditTime = () => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const nowDate = `${pad(now.getDate())}.${pad(now.getMonth() + 1)}.${now.getFullYear()}`;
    const nowTime = `${pad(now.getHours())}:${pad(now.getMinutes())}`;
    setForm(prev => ({
      ...prev,
      editTime: !prev.editTime,
      paid_at_date: prev.editTime ? prev.paid_at_date : (prev.paid_at_date || nowDate),
      paid_at_time: prev.editTime ? prev.paid_at_time : (prev.paid_at_time || nowTime),
    }));
  };

  const handleSubmit = async () => {
    if (!form.method) { setForm(p => ({ ...p, error: 'Выберите способ оплаты' })); return; }
    if (!form.paid_at_date || form.paid_at_date.length < 10 || !form.paid_at_time || form.paid_at_time.length < 5) {
      setForm(p => ({ ...p, error: 'Введите дату и время оплаты' })); return;
    }
    const dateParts = form.paid_at_date.split('.');
    const isoDate = `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`;
    const selectedDateTime = new Date(`${isoDate}T${form.paid_at_time}`);
    if (selectedDateTime > new Date()) {
      setForm(p => ({ ...p, error: 'Дата и время не могут быть в будущем' })); return;
    }
    try {
      await onConfirm({
        method: form.method,
        paid_at: selectedDateTime.toISOString(),
        comment: form.comment.trim() || null,
      });
    } catch (e) {
      setForm(p => ({ ...p, error: e?.message || 'Ошибка обновления статуса оплаты' }));
    }
  };

  return (
    <div style={{ marginTop: 12, padding: '18px 20px', background: '#fff', '--ch': '10px', '--stroke': '#1C3D2E', border: '1px solid #1C3D2E', boxShadow: '0 2px 12px rgba(28,61,46,0.08)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#1C3D2E', letterSpacing: '0.3px' }}>{title}</div>
        <button onClick={onClose}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#5E6561', fontSize: 20, lineHeight: 1, padding: '0 2px' }}>&times;</button>
      </div>

      {/* Способ оплаты — переключатель «одно из» (UX-аудит 25.10) */}
      <div className="seg" role="group" aria-label="Способ оплаты" style={{ display: 'flex', width: '100%', marginBottom: 14 }}>
        {methods.map(m => {
          const isActive = form.method === m.key;
          return (
            <button
              key={m.key} type="button" aria-pressed={isActive}
              onClick={() => setForm(prev => ({ ...prev, method: m.key, error: '' }))}
              className={`seg-btn${isActive ? ' on' : ''}`}
              style={{ flex: 1 }}
            >
              {m.label}
            </button>
          );
        })}
      </div>

      {/* Дата/время — переключатель «Сейчас» / карандаш (вручную) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <div className="seg" role="group" aria-label="Время оплаты">
          <button type="button" onClick={setNow} aria-pressed={!!form.paid_at_date && !form.editTime}
            className={`seg-btn${form.paid_at_date && !form.editTime ? ' on' : ''}`}>
            Сейчас
          </button>
          <button type="button" title="Указать вручную" aria-label="Указать вручную" onClick={toggleEditTime} aria-pressed={form.editTime}
            className={`seg-btn${form.editTime ? ' on' : ''}`} style={{ display: 'inline-flex', alignItems: 'center' }}>
            <EditIcon size={14} />
          </button>
        </div>
        {form.paid_at_date && !form.editTime && (
          <span style={{ fontSize: 13, color: '#1C3D2E', fontWeight: 500 }}>{form.paid_at_date}, {form.paid_at_time}</span>
        )}
      </div>

      {form.editTime && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <input type="text" inputMode="numeric" placeholder="ДД.ММ.ГГГГ"
            value={form.paid_at_date || ''}
            onChange={(e) => {
              let digits = e.target.value.replace(/\D/g, '');
              if (digits.length > 8) digits = digits.slice(0, 8);
              let formatted = '';
              if (digits.length > 0) formatted += digits.slice(0, 2);
              if (digits.length > 2) formatted += '.' + digits.slice(2, 4);
              if (digits.length > 4) formatted += '.' + digits.slice(4, 8);
              setForm(prev => ({ ...prev, paid_at_date: formatted, error: '' }));
            }}
            style={{ flex: 1, padding: '8px 12px', border: '1px solid #D8DDDA', '--ch': '6px', '--stroke': '#D8DDDA', fontSize: 14, color: '#1C3D2E' }}
          />
          <input type="text" inputMode="numeric" placeholder="ЧЧ:ММ"
            value={form.paid_at_time || ''}
            onChange={(e) => {
              let digits = e.target.value.replace(/\D/g, '');
              if (digits.length > 4) digits = digits.slice(0, 4);
              let formatted = '';
              if (digits.length > 0) formatted += digits.slice(0, 2);
              if (digits.length > 2) formatted += ':' + digits.slice(2, 4);
              setForm(prev => ({ ...prev, paid_at_time: formatted, error: '' }));
            }}
            style={{ width: 100, padding: '8px 12px', border: '1px solid #D8DDDA', '--ch': '6px', '--stroke': '#D8DDDA', fontSize: 14, color: '#1C3D2E' }}
          />
        </div>
      )}

      {/* Комментарий */}
      <input type="text" value={form.comment}
        onChange={(e) => setForm(prev => ({ ...prev, comment: e.target.value }))}
        placeholder={commentPlaceholder}
        style={{ width: '100%', padding: '9px 12px', border: '1px solid #D8DDDA', '--ch': '6px', '--stroke': '#D8DDDA', fontSize: 13, marginBottom: 12, boxSizing: 'border-box', color: '#1C3D2E' }}
      />

      {extraSlot}

      {form.error && (
        <div style={{ padding: '8px 12px', marginBottom: 10, background: '#FFE7DD', '--ch': '6px', '--stroke': '#ED956F', fontSize: 13, color: '#B0603C', border: '1px solid #ED956F' }}>
          {form.error}
        </div>
      )}

      {/* Общий порядок: [Отмена] [главная] справа (UX-аудит 25.10) */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
        <button type="button" className="btn btn-secondary btn-md" onClick={onClose} disabled={busy}>
          Отмена
        </button>
        <button type="button" className="btn btn-primary btn-md" onClick={handleSubmit} disabled={busy}>
          Подтвердить оплату
        </button>
      </div>
    </div>
  );
}
